import InfiniteScroll from "react-infinite-scroll-component";
import { AccessLevel } from "@/api/williams/users";
import { ExpandableCard, PinButton } from "../../components";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { faSquarePlus } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getFullPath, routes } from "../../routes";
import { highlight } from "@/util/Highlighter";
import { Link } from "react-router-dom";
import { ProjectFilters, ProjectListView } from "@/api/williams/project";
import { String } from "@/util";
import { usePermissions } from "../../users/hooks";
import { usePinProject, useProjectList } from "../hooks";
import { useState } from "react";

export type ProjectsProps = {
  filters: Partial<ProjectFilters>;
  expanded?: boolean;
  allowExpand?: boolean;
};

export function Projects({ filters, ...props }: ProjectsProps) {
  const projects = useProjectList(filters);
  const permissions = usePermissions();

  const data = projects.data?.pages.flatMap((page) => page.results) ?? [];
  const total = projects.data?.pages[0]?.total ?? 0;
  const readOnly = permissions.data?.accessLevel === AccessLevel.GeneralUser;

  return (
    <ExpandableCard
      caption={`Projects (${total})`}
      expanded={props.expanded}
      allowExpand={props.allowExpand}
      headerButtons={[
        <Link to={getFullPath(routes.project.create)} hidden={readOnly}>
          <FontAwesomeIcon icon={faSquarePlus} size="sm" title="Add Project" />
        </Link>,
      ]}
    >
      <InfiniteScroll
        dataLength={data.length}
        next={projects.fetchNextPage}
        hasMore={Boolean(projects.hasNextPage)}
        loader={<p>Loading...</p>}
      >
        {projects.isLoading ? (
          <p>Loading...</p>
        ) : projects.isError ? (
          <p>An unknown error occurred.</p>
        ) : projects.isSuccess && data.length > 0 ? (
          <ProjectsTable data={data} search={filters.searchString} />
        ) : filters.searchString ? (
          <p>Your search returned no results.</p>
        ) : (
          <p>No projects found.</p>
        )}
      </InfiniteScroll>
    </ExpandableCard>
  );
}

function ProjectsTable({ data, search }: { data: ProjectListView[]; search?: string }) {
  return (
    <table className="table table-sm table-borderless mb-0">
      <thead>
        <tr>
          <th className="col col-md-4">Name</th>
          <th className="col-4 d-none d-md-table-cell">Number</th>
          <th className="col-4 d-none d-md-table-cell">Description</th>
          <th className="col-auto" />
        </tr>
      </thead>
      <tbody>
        {data.map((project) => (
          <ProjectRow key={project.id} project={project} search={search} />
        ))}
      </tbody>
    </table>
  );
}

function ProjectRow({ project, search }: { project: ProjectListView; search?: string }) {
  const [expanded, setExpanded] = useState<boolean>(false);

  const pin = usePinProject();

  return (
    <>
      <tr key={`${project.id}_${project.pinned}`}>
        <td>
          <Link to={getFullPath(routes.project.open(project.id))} className="link link-primary">
            {highlight(String.truncate(project.name, 50), search)}
          </Link>
        </td>
        <td className="d-none d-md-table-cell">{highlight(String.truncate(project.number, 50), search)}</td>
        <td className="d-none d-md-table-cell">{highlight(String.truncate(project.description, 50), search)}</td>
        <td className="text-end text-nowrap">
          <FontAwesomeIcon
            role="button"
            icon={expanded ? faChevronUp : faChevronDown}
            size="sm"
            className="me-1"
            title="Details"
            onClick={() => setExpanded((prev) => !prev)}
            fixedWidth={true}
          />
          <PinButton pinned={project.pinned} onChange={() => pin.mutate(project.id)} />
        </td>
      </tr>
      {expanded && (
        <tr>
          <td className="d-none d-md-table-cell" colSpan={4}>
            <ExpandedDetails project={project} search={search} />
          </td>
          <td className="d-table-cell d-md-none" colSpan={2}>
            <ExpandedDetails project={project} search={search} />
          </td>
        </tr>
      )}
    </>
  );
}

function ExpandedDetails({ project, search }: { project: ProjectListView; search?: string }) {
  return (
    <div className="ms-1">
      <div className="bg-light p-1">Project Details</div>
      <div className="p-1">
        <dl>
          <dt className="d-block d-md-none">Number</dt>
          <dd className="d-block d-md-none">{highlight(project.number, search)}</dd>
          <dt>Status</dt>
          <dd>Active</dd>
          <dt>Description</dt>
          <dd>{highlight(project.description, search)}</dd>
        </dl>
      </div>
    </div>
  );
}
